import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  MapPin, 
  Clock, 
  User, 
  Phone,
  Navigation,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Truck,
  Maximize2,
  Wifi
} from 'lucide-react';
import { useLiveTrackingData, useBookingStatusHistory } from '../../hooks/useBooking';
import { useBookingWebSocket } from '../../hooks/useWebSocket';
import webSocketService from '../../services/webSocketService';
import TrackingMap from '../maps/TrackingMap';

// Helper function to check if booking is scheduled
const isScheduledBooking = (bookingType) => {
  if (!bookingType) return false;
  const upperType = bookingType.toUpperCase();
  const lowerType = bookingType.toLowerCase();
  return upperType === 'SCHEDULED' || lowerType === 'scheduled';
};

// Helper function to extract status history from response
const extractStatusHistory = (statusHistoryResponse) => {
  if (statusHistoryResponse?.data && Array.isArray(statusHistoryResponse.data)) {
    return statusHistoryResponse.data;
  }
  if (statusHistoryResponse?.data?.data && Array.isArray(statusHistoryResponse.data.data)) {
    return statusHistoryResponse.data.data;
  }
  if (Array.isArray(statusHistoryResponse)) {
    return statusHistoryResponse;
  }
  return [];
};

// Helper function to check if error is "not found"
const isNotFoundError = (trackingError) => {
  if (!trackingError) return false;

  const errorMessage = trackingError.message || trackingError.error?.message || JSON.stringify(trackingError);
  const lowerMessage = errorMessage.toLowerCase();
  const lowerError = trackingError.error?.toLowerCase() || '';

  return trackingError.status === 404 ||
         trackingError.response?.status === 404 ||
         lowerMessage.includes('not found') ||
         lowerMessage.includes('no booking') ||
         lowerMessage.includes('booking not found') ||
         lowerError.includes('not found');
};

// Helper function to render error-no-id state
const renderErrorNoIdState = (onClose) => (
  <Card className="w-full max-w-2xl mx-auto">
    <CardContent className="p-6">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
        <p className="text-lg font-medium text-gray-900 mb-2">Booking Successful!</p>
        <p className="text-gray-600 mb-4">
          Your ambulance has been booked successfully, but we couldn't retrieve the tracking ID.
        </p>
        <p className="text-sm text-gray-500">
          Please contact support or check your booking history for tracking information.
        </p>
        {onClose && (
          <Button onClick={onClose} className="mt-4">
            Close
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

// Helper function to render loading state
const renderLoadingState = () => (
  <Card className="w-full max-w-2xl mx-auto">
    <CardContent className="p-6">
      <div className="flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Loading tracking data...</span>
      </div>
    </CardContent>
  </Card>
);

// Helper function to render error state
const renderErrorState = (trackingError, bookingId, refetch, onClose) => {
  const notFound = isNotFoundError(trackingError);
  const errorMessage = trackingError.message || trackingError.error?.message || JSON.stringify(trackingError);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardContent className="p-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {notFound ? 'Booking Not Found' : 'Error Loading Tracking Data'}
          </p>
          <p className="text-gray-600 mb-4">
            {notFound
              ? `No booking found with ID: ${bookingId}. Please verify the booking ID and try again.`
              : errorMessage || 'Failed to load tracking information'
            }
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={refetch} className="bg-blue-600 hover:bg-blue-700">
              <RefreshCw className="w-4 h-4 mr-1" />
              Try Again
            </Button>
            {onClose && (
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Helper function to render no data state
const renderNoDataState = (bookingId, refetch, onClose) => (
  <Card className="w-full max-w-2xl mx-auto">
    <CardContent className="p-6">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
        <p className="text-lg font-medium text-gray-900 mb-2">No Tracking Data Available</p>
        <p className="text-gray-600 mb-4">
          Unable to retrieve tracking information for booking ID: {bookingId}. 
          The booking may not exist or may not be accessible.
        </p>
        <div className="flex gap-3 justify-center">
          <Button onClick={refetch} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4 mr-1" />
            Try Again
          </Button>
          {onClose && (
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const BookingTrackingCard = ({ bookingId, onClose, journeyDirection: journeyDirectionProp }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [realtimeDriverLocation, setRealtimeDriverLocation] = useState(null);
  const [isReceivingLiveUpdates, setIsReceivingLiveUpdates] = useState(false);
  const [lastWebSocketUpdate, setLastWebSocketUpdate] = useState(null);
  
  console.log("BookingTrackingCard rendered with bookingId:", bookingId)
  
  // WebSocket for real-time updates (instant)
  // Only call hook if bookingId is valid, but always call it to maintain hook order
  const { 
    locationUpdates, 
    isTracking: isWebSocketTracking 
  } = useBookingWebSocket(bookingId && bookingId !== 'error-no-id' ? bookingId : null);
  
  // Polling as fallback (every 3 seconds)
  // New endpoint: /v1/hospital/bookings/:bookingId/tracking
  // Response structure: { success: true, message: "...", data: { booking_id: "...", ambulance_location: null, status: "...", eta_minutes: null, distance_km: null } }
  // The service transforms this to: { bookingId, status, tracking: { eta, distance, driverLocation } }
  // Poll every 3 seconds for real-time updates
  // Only call hook if bookingId is valid, but always call it to maintain hook order
  const { data: trackingData, loading: trackingLoading, error: trackingError, refetch } = useLiveTrackingData(
    bookingId && bookingId !== 'error-no-id' ? bookingId : null, 
    3000
  );
  
  // New endpoint: /v1/hospital/bookings/:bookingId/status-history
  // Response structure: { success: true, message: "...", data: [{ id: "...", old_status: null, new_status: "...", changed_by: "...", reason: null, created_at: "..." }] }
  // The service transforms this to: { success: true, message: "...", data: [{ id, oldStatus, newStatus, changedBy, reason, changedAt }] }
  // Only call hook if bookingId is valid, but always call it to maintain hook order
  const { data: statusHistoryResponse, loading: historyLoading } = useBookingStatusHistory(
    bookingId && bookingId !== 'error-no-id' ? bookingId : null
  );
  
  const statusHistory = extractStatusHistory(statusHistoryResponse);
  
  console.log("Tracking data:", trackingData)
  console.log("Tracking loading:", trackingLoading)
  console.log("Tracking error:", trackingError)
  console.log("🔴 WebSocket tracking active:", isWebSocketTracking)
  console.log("🔴 Location updates count:", locationUpdates?.length || 0)

  // Listen for WebSocket location updates (real-time driver location)
  useEffect(() => {
    if (!bookingId) return;

    // Handler for driver_location_updated event (per integration doc structure)
    const handleDriverLocationUpdated = (data) => {
      console.log('🚗💨 [BookingTrackingCard] driver_location_updated received via WebSocket:', data);
      
      // Check if this update is for our booking
      if (data.bookingId === bookingId || data.booking_id === bookingId) {
        const tracking = data.tracking;
        if (tracking?.driverLocation) {
          const loc = tracking.driverLocation;
          const lat = parseFloat(loc.latitude || loc.lat);
          const lng = parseFloat(loc.longitude || loc.lng);
          
          if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
              lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            
            const newLocation = {
              latitude: lat,
              longitude: lng,
              lastUpdated: new Date().toISOString(),
              heading: loc.heading || null,
              bearing: loc.bearing || null,
              speed: loc.speed || null,
              accuracy: loc.accuracy || null
            };
            
            console.log('✅ [BookingTrackingCard] Updating driver location from WebSocket:', newLocation);
            setRealtimeDriverLocation(newLocation);
            setIsReceivingLiveUpdates(true);
            setLastWebSocketUpdate(new Date());
            
            // Reset the "live updates" indicator after 5 seconds
            setTimeout(() => setIsReceivingLiveUpdates(false), 5000);
          } else {
            console.warn('⚠️ [BookingTrackingCard] Invalid coordinates from WebSocket:', { lat, lng });
          }
        }
      }
    };

    // Handler for legacy driver_location_update event (backwards compatibility)
    const handleDriverLocationUpdate = (data) => {
      console.log('🚗💨 [BookingTrackingCard] Real-time driver location received via WebSocket:', data);
      
      // Check if this update is for our booking
      if (data.bookingId === bookingId || data.booking_id === bookingId) {
        const lat = parseFloat(data.latitude || data.lat);
        const lng = parseFloat(data.longitude || data.lng || data.lon);
        
        if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          
          const newLocation = {
            latitude: lat,
            longitude: lng,
            lastUpdated: data.timestamp || data.lastUpdated || new Date().toISOString(),
            heading: data.heading || null,
            bearing: data.bearing || null,
            speed: data.speed || null,
            accuracy: data.accuracy || null
          };
          
          console.log('✅ [BookingTrackingCard] Updating driver location from WebSocket:', newLocation);
          setRealtimeDriverLocation(newLocation);
          setIsReceivingLiveUpdates(true);
          setLastWebSocketUpdate(new Date());
          
          // Reset the "live updates" indicator after 5 seconds
          setTimeout(() => setIsReceivingLiveUpdates(false), 5000);
        } else {
          console.warn('⚠️ [BookingTrackingCard] Invalid coordinates from WebSocket:', { lat, lng });
        }
      }
    };

    const handleLocationUpdate = (data) => {
      console.log('📍 [BookingTrackingCard] Location update received via WebSocket:', data);
      // Handle generic location updates (same logic as driver location)
      handleDriverLocationUpdate(data);
    };

    // Register WebSocket event listeners
    webSocketService.on('driver_location_updated', handleDriverLocationUpdated);
    webSocketService.on('driver_location_update', handleDriverLocationUpdate);
    webSocketService.on('location_update', handleLocationUpdate);

    return () => {
      // Cleanup event listeners
      webSocketService.off('driver_location_updated', handleDriverLocationUpdated);
      webSocketService.off('driver_location_update', handleDriverLocationUpdate);
      webSocketService.off('location_update', handleLocationUpdate);
    };
  }, [bookingId]);

  // Process location updates from useBookingWebSocket hook
  useEffect(() => {
    if (locationUpdates && locationUpdates.length > 0) {
      const latestUpdate = locationUpdates[locationUpdates.length - 1];
      console.log('📍 [BookingTrackingCard] Processing location update from hook:', latestUpdate);
      
      if (latestUpdate.latitude && latestUpdate.longitude) {
        const lat = parseFloat(latestUpdate.latitude);
        const lng = parseFloat(latestUpdate.longitude);
        
        if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) &&
            lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          
          setRealtimeDriverLocation({
            latitude: lat,
            longitude: lng,
            lastUpdated: latestUpdate.timestamp || new Date().toISOString(),
            heading: latestUpdate.heading || null,
            bearing: latestUpdate.bearing || null,
            speed: latestUpdate.speed || null
          });
          setIsReceivingLiveUpdates(true);
          setLastWebSocketUpdate(new Date());
          
          setTimeout(() => setIsReceivingLiveUpdates(false), 5000);
        }
      }
    }
  }, [locationUpdates]);

  // Auto-refresh every 30 seconds (polling fallback)
  useEffect(() => {
    const interval = setInterval(() => {
      if (refetch && typeof refetch === 'function') {
        refetch();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refetch]);

  // Helper function to get status color
  const getStatusColor = (status, bookingType) => {
    if (isScheduledBooking(bookingType)) {
      return 'bg-indigo-100 text-indigo-800';
    }
    
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'dispatched':
        return 'bg-purple-100 text-purple-800';
      case 'in_transit':
      case 'in_progress':
        return 'bg-orange-100 text-orange-800';
      case 'arrived':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'scheduled':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get status display text
  const getStatusDisplay = (status, bookingType, scheduledDateTime) => {
    if (!isScheduledBooking(bookingType)) {
      return status || 'Pending';
    }

    if (scheduledDateTime) {
      const scheduledDate = new Date(scheduledDateTime);
      const now = new Date();
      if (scheduledDate > now) {
        return 'Booking is scheduled';
      }
      return status || 'Scheduled';
    }

    return 'Booking is scheduled';
  };

  // Helper function to format time
  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString();
  };

  // Helper function to check if scheduled date is in future
  const isScheduledDateInFuture = (scheduledDateTime) => {
    if (!scheduledDateTime) return false;
    const scheduledDate = new Date(scheduledDateTime);
    const now = new Date();
    return scheduledDate > now;
  };

  // Helper function to format ETA
  const formatETA = (eta, bookingType, scheduledDateTime) => {
    if (isScheduledBooking(bookingType) && scheduledDateTime && isScheduledDateInFuture(scheduledDateTime)) {
      const scheduledDate = new Date(scheduledDateTime);
      return scheduledDate.toLocaleString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    if (!eta) return 'Calculating...';
    
    if (typeof eta === 'number') {
      return eta > 0 ? `${eta} min` : 'Arriving soon';
    }
    
    const now = new Date();
    const diff = new Date(eta) - now;
    const minutes = Math.floor(diff / (1000 * 60));
    return minutes > 0 ? `${minutes} min` : 'Arriving soon';
  };

  // Helper function to format distance
  const formatDistance = (distance) => {
    if (!distance) return 'N/A';
    if (typeof distance === 'number') {
      return `${distance.toFixed(1)} km`;
    }
    return `${distance} km`;
  };

  // Helper function to get booking type display
  const getBookingTypeDisplay = (bookingType) => {
    if (!bookingType) return 'Instant';
    if (isScheduledBooking(bookingType)) {
      return 'Scheduled';
    }
    return bookingType;
  };

  // Helper function to get ETA label
  const getETALabel = (bookingType, scheduledDateTime) => {
    if (isScheduledBooking(bookingType) && scheduledDateTime) {
      return 'Scheduled For';
    }
    return 'ETA';
  };

  // Helper function to render scheduled booking notice
  const renderScheduledBookingNotice = (trackingData) => {
    if (!isScheduledBooking(trackingData.bookingType) || !trackingData.scheduledDateTime) {
      return null;
    }

    if (!isScheduledDateInFuture(trackingData.scheduledDateTime)) {
      return null;
    }

    const scheduledDate = new Date(trackingData.scheduledDateTime);
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <Clock className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-indigo-900 mb-1">Booking is Scheduled</h3>
            <p className="text-sm text-indigo-700">
              This booking is scheduled for{' '}
              <span className="font-medium">
                {scheduledDate.toLocaleString('en-IN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </p>
            <p className="text-xs text-indigo-600 mt-1">
              Tracking information will be available when the booking becomes active.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render scheduled date time
  const renderScheduledDateTime = (trackingData) => {
    if (!isScheduledBooking(trackingData.bookingType) || !trackingData.scheduledDateTime) {
      return null;
    }

    return (
      <div className="col-span-2">
        <span className="text-gray-500">Scheduled Date & Time:</span>
        <p className="text-gray-900">
          {new Date(trackingData.scheduledDateTime).toLocaleString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>
    );
  };

  // Helper function to get status history text
  const getStatusHistoryText = (history) => {
    const newStatus = history.newStatus || history.new_status;
    const oldStatus = history.oldStatus || history.old_status;
    
    if (oldStatus) {
      return `${newStatus} (from ${oldStatus})`;
    }
    return newStatus;
  };

  // Helper function to get status history details
  const getStatusHistoryDetails = (history) => {
    const reason = history.reason || 'No reason provided';
    const changedAt = history.changedAt || history.created_at || history.changed_at;
    const changedBy = history.changedBy || history.changed_by;
    
    let details = `${reason} • ${formatTime(changedAt)}`;
    if (changedBy) {
      details += ` • by ${changedBy}`;
    }
    return details;
  };

  // Extract pickup location from tracking data
  // Always call useMemo hooks before conditional returns to maintain hook order
  const pickupLocation = useMemo(() => {
    if (!trackingData?.pickupLocation) return null;
    
    const lat = parseFloat(trackingData.pickupLocation.latitude || trackingData.pickupLocation.lat);
    const lng = parseFloat(trackingData.pickupLocation.longitude || trackingData.pickupLocation.lng);
    
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    
    return {
      latitude: lat,
      longitude: lng,
      address: trackingData.pickupLocation.address || 'Pickup Location'
    };
  }, [trackingData?.pickupLocation]);

  // Extract dropoff location from tracking data
  const dropoffLocation = useMemo(() => {
    if (!trackingData?.dropoffLocation) return null;
    
    const lat = parseFloat(trackingData.dropoffLocation.latitude || trackingData.dropoffLocation.lat);
    const lng = parseFloat(trackingData.dropoffLocation.longitude || trackingData.dropoffLocation.lng);
    
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    
    return {
      latitude: lat,
      longitude: lng,
      address: trackingData.dropoffLocation.address || 'Dropoff Location'
    };
  }, [trackingData?.dropoffLocation]);

  // Extract ambulance/driver location from tracking data
  // Priority: WebSocket real-time updates > Polling data
  const ambulanceLocation = useMemo(() => {
    console.log('🔍 [ambulanceLocation memo] Calculating driver location');
    console.log('  - Has realtime location:', !!realtimeDriverLocation);
    console.log('  - Has polling location:', !!(trackingData?.tracking?.driverLocation || trackingData?.tracking?.ambulanceLocation));
    
    // PRIORITY 1: Real-time WebSocket location (most up-to-date)
    if (realtimeDriverLocation && realtimeDriverLocation.latitude && realtimeDriverLocation.longitude) {
      console.log('✅ [ambulanceLocation] Using WebSocket real-time location:', realtimeDriverLocation);
      return realtimeDriverLocation;
    }
    
    // PRIORITY 2: Polling location (fallback)
    const driverLoc = trackingData?.tracking?.driverLocation || trackingData?.tracking?.ambulanceLocation;
    if (!driverLoc || !driverLoc.latitude || !driverLoc.longitude) {
      console.log('⚠️ [ambulanceLocation] No driver location available');
      return null;
    }
    
    const lat = parseFloat(driverLoc.latitude);
    const lng = parseFloat(driverLoc.longitude);
    
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      console.log('⚠️ [ambulanceLocation] Invalid coordinates from polling:', { lat, lng });
      return null;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.log('⚠️ [ambulanceLocation] Coordinates out of range:', { lat, lng });
      return null;
    }
    
    console.log('✅ [ambulanceLocation] Using polling location:', { lat, lng });
    return {
      latitude: lat,
      longitude: lng,
      lastUpdated: driverLoc.lastUpdated || null,
      heading: driverLoc.heading || null,
      bearing: driverLoc.bearing || null
    };
  }, [realtimeDriverLocation, trackingData?.tracking?.driverLocation, trackingData?.tracking?.ambulanceLocation]);

  // Handle early returns AFTER all hooks are called
  if (!bookingId) {
    console.log("No bookingId provided to BookingTrackingCard")
    return null;
  }

  if (bookingId === 'error-no-id') {
    return renderErrorNoIdState(onClose);
  }

  if (trackingLoading) {
    return renderLoadingState();
  }

  if (trackingError) {
    return renderErrorState(trackingError, bookingId, refetch, onClose);
  }

  if (!trackingData && !trackingLoading && !trackingError) {
    return renderNoDataState(bookingId, refetch, onClose);
  }

  if (!trackingData) {
    return null;
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold text-gray-900">
            Booking Tracking
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Badge className={getStatusColor(trackingData.status, trackingData.bookingType)}>
              {getStatusDisplay(trackingData.status, trackingData.bookingType, trackingData.scheduledDateTime)}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>
            {onClose && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Live Tracking Map */}
        {(pickupLocation || dropoffLocation || ambulanceLocation) && (
          <div className="relative h-64 sm:h-80 lg:h-96 rounded-lg border border-gray-300 overflow-hidden bg-gray-100">
            <TrackingMap
              pickupLocation={pickupLocation}
              dropoffLocation={dropoffLocation}
              ambulanceLocation={ambulanceLocation}
              distance={trackingData.tracking?.distance}
              eta={trackingData.tracking?.eta}
              status={trackingData.status}
              journeyDirection={journeyDirectionProp || trackingData.journey_direction || trackingData.journeyDirection || trackingData.direction || 'OUTBOUND'}
              zoom={13}
              showControls={true}
              height="100%"
            />
            {/* Real-time Live indicator - shows data source */}
            {ambulanceLocation && (
              <div className={`absolute top-2 left-2 flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs font-medium z-30 shadow-lg transition-all duration-300 ${
                isReceivingLiveUpdates 
                  ? 'bg-green-500 text-white scale-105' 
                  : realtimeDriverLocation 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-500 text-white'
              }`}>
                {isReceivingLiveUpdates ? (
                  <>
                    <Wifi className="w-3 h-3 animate-pulse" />
                    <span className="font-bold">LIVE UPDATE</span>
                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                  </>
                ) : realtimeDriverLocation ? (
                  <>
                    <Wifi className="w-3 h-3" />
                    <span>REAL-TIME</span>
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  </>
                ) : (
                  <>
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                    <span>TRACKING</span>
                  </>
                )}
              </div>
            )}
            {/* WebSocket status indicator */}
            {isWebSocketTracking && (
              <div className="absolute top-2 right-2 flex items-center space-x-1 bg-white/90 backdrop-blur-sm text-green-600 px-2 py-1 rounded text-xs font-medium z-30 border border-green-200">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span>WebSocket Active</span>
              </div>
            )}
            {/* Last update timestamp */}
            {lastWebSocketUpdate && (
              <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs z-30">
                Last update: {Math.floor((new Date() - lastWebSocketUpdate) / 1000)}s ago
              </div>
            )}
          </div>
        )}

        {/* Scheduled Booking Notice */}
        {renderScheduledBookingNotice(trackingData)}
        
        {/* Booking Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Pickup Location</span>
            </div>
            <p className="text-sm text-gray-600 pl-6">
              {trackingData.pickupLocation?.address || 'N/A'}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">
                {getETALabel(trackingData.bookingType, trackingData.scheduledDateTime)}
              </span>
            </div>
            <p className="text-sm text-gray-600 pl-6">
              {formatETA(trackingData.tracking?.eta, trackingData.bookingType, trackingData.scheduledDateTime)}
            </p>
          </div>
        </div>

        {/* Driver & Ambulance Info */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Driver & Ambulance Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Driver</span>
              </div>
              <div className="pl-6 space-y-1">
                <p className="text-sm text-gray-900">{trackingData.driver?.name || 'N/A'}</p>
                <p className="text-xs text-gray-500">
                  Experience: {trackingData.driver?.experienceLevel || 'N/A'}
                </p>
                {trackingData.driver?.phone && (
                  <div className="flex items-center space-x-1">
                    <Phone className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">{trackingData.driver.phone}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Truck className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Ambulance</span>
              </div>
              <div className="pl-6 space-y-1">
                <p className="text-sm text-gray-900">
                  {trackingData.ambulance?.plateNumber || 'N/A'}
                </p>
                <p className="text-xs text-gray-500">
                  Type: {trackingData.ambulance?.type || 'Standard'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t pt-4 space-y-4">
            {/* Location Tracking */}
            {trackingData.tracking?.driverLocation && (
              <div className="space-y-2">
                <h4 className="text-md font-medium text-gray-900">Current Location</h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Navigation className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Driver Location</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Lat: {trackingData.tracking.driverLocation.latitude?.toFixed(6)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Lng: {trackingData.tracking.driverLocation.longitude?.toFixed(6)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last updated: {formatTime(trackingData.tracking.driverLocation.lastUpdated)}
                  </p>
                </div>
              </div>
            )}

            {/* Status History */}
            {statusHistory && statusHistory.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-md font-medium text-gray-900">Status History</h4>
                <div className="space-y-2">
                  {statusHistory.slice(0, 3).map((history, index) => (
                    <div key={history.id || index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                      <div className="flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          {getStatusHistoryText(history)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getStatusHistoryDetails(history)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Booking Details */}
            <div className="space-y-2">
              <h4 className="text-md font-medium text-gray-900">Booking Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Booking ID:</span>
                  <p className="font-mono text-xs">{trackingData.bookingId}</p>
                </div>
                <div>
                  <span className="text-gray-500">Booking Type:</span>
                  <p className="text-gray-900">
                    {getBookingTypeDisplay(trackingData.bookingType)}
                  </p>
                </div>
                {renderScheduledDateTime(trackingData)}
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="text-gray-900">{formatTime(trackingData.createdAt)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Hospital:</span>
                  <p className="text-gray-900">{trackingData.hospital?.name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Distance:</span>
                  <p className="text-gray-900">
                    {formatDistance(trackingData.tracking?.distance)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-xs text-gray-500">
            Last updated: {formatTime(trackingData.tracking?.lastUpdated)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (refetch && typeof refetch === 'function') {
                refetch();
              }
            }}
            className="text-blue-600 hover:text-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingTrackingCard; 